var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var _a, _b, _c, _d;
import { css, html, LitElement } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { Demodulator, } from "@jtarrio/signals/demod/demodulator.js";
import { SampleCounter, } from "@jtarrio/signals/demod/sample-counter.js";
import { getMode, getSchemes, modeParameters, } from "@jtarrio/signals/demod/modes.js";
import { Spectrum } from "@jtarrio/signals/demod/spectrum.js";
import { Float32Pool } from "@jtarrio/signals/dsp/buffers.js";
import { RadioErrorType } from "@jtarrio/webrtlsdr/errors.js";
import { Radio, RtlProvider } from "@jtarrio/webrtlsdr/radio.js";
import { CompositeReceiver } from "@jtarrio/signals/radio.js";
import { DirectSampling } from "@jtarrio/webrtlsdr/rtlsdr.js";
import { loadConfig } from "./config.js";
import { RrPresets, } from "./presets.js";
import { RrMainControls } from "./main-controls.js";
import { RrSettings, } from "./settings.js";
import { CreateWindowRegistry, } from "../../ui/controls/window.js";
import { BaseStyle } from "../../ui/styles.js";
import { RrSpectrum } from "../../ui/spectrum/spectrum.js";
import "./main-controls.js";
import "./settings.js";
import "./presets.js";
import "../../ui/spectrum/spectrum.js";
let RadioReceiverMain = class RadioReceiverMain extends LitElement {
    static get styles() {
        return [
            BaseStyle,
            css `
        :host {
          height: 100%;
          display: flex;
          flex-direction: column;
          box-sizing: border-box;
          touch-action: none;
        }

        #spectrum {
          width: 100%;
          height: 0;
          flex: 1;
          margin: 0;
        }
      `,
        ];
    }
    render() {
        return html `<rr-spectrum
        id="spectrum"
        .minDecibels=${this.minDecibels}
        .maxDecibels=${this.maxDecibels}
        .centerFrequency=${this.frequency.center}
        .bandwidth=${this.bandwidth}
        .frequencyScale=${this.scale}
        .highlight=${{
            point: this.frequency.offset / this.bandwidth + 0.5,
            band: {
                left: (this.frequency.offset - this.frequency.leftBand) /
                    this.bandwidth +
                    0.5,
                right: (this.frequency.offset + this.frequency.rightBand) /
                    this.bandwidth +
                    0.5,
            },
        }}
        .highlightDraggablePoint=${true}
        .highlightDraggableLeft=${this.mode.scheme != "WBFM" &&
            this.mode.scheme != "USB"}
        .highlightDraggableRight=${this.mode.scheme != "WBFM" &&
            this.mode.scheme != "LSB"}
        .waterfallDraggable=${true}
        @spectrum-tap=${this.onSpectrumTap}
        @spectrum-drag=${this.onSpectrumDrag}
        @spectrum-highlight-changed=${this.onSpectrumHighlightChanged}
        @spectrum-decibel-range-changed=${this.onDecibelRangeChanged}
      ></rr-spectrum>

      <rr-main-controls
        .position=${this.windowState.controls.position}
        .playing=${this.playing}
        .needsReload=${this.needsReload || this.errorState}
        .centerFrequency=${this.frequency.center}
        .tunedFrequency=${this.frequency.center + this.frequency.offset}
        .tuningStep=${this.tuningStep}
        .scale=${this.scale}
        .availableModes=${getSchemes()}
        .scheme=${this.mode.scheme}
        .bandwidth=${modeParameters(this.mode).getBandwidth()}
        .stereo=${modeParameters(this.mode).getStereo()}
        .squelch=${modeParameters(this.mode).getSquelch()}
        .stereoStatus=${this.stereoStatus}
        .gain=${this.gain}
        .gainDisabled=${this.gainDisabled}
        @rr-start=${this.onStart}
        @rr-stop=${this.onStop}
        @rr-presets=${this.onPresets}
        @rr-settings=${this.onSettings}
        @rr-scale-changed=${this.onScaleChange}
        @rr-center-frequency-changed=${this.onCenterFrequencyChange}
        @rr-tuned-frequency-changed=${this.onTunedFrequencyChange}
        @rr-tuning-step-changed=${this.onTuningStepChange}
        @rr-scheme-changed=${this.onSchemeChange}
        @rr-bandwidth-changed=${this.onBandwidthChange}
        @rr-stereo-changed=${this.onStereoChange}
        @rr-squelch-changed=${this.onSquelchChange}
        @rr-gain-changed=${this.onGainChange}
        @rr-window-moved=${this.onWindowMoved}
      ></rr-main-controls>

      <rr-settings
        .closed=${!this.windowState.settings.open}
        .position=${this.windowState.settings.position}
        .playing=${this.playing}
        .sampleRate=${this.sampleRate}
        .ppm=${this.ppm}
        .fftSize=${this.fftSize}
        .fmDeemph=${this.fmDeemph}
        .biasTee=${this.biasTee}
        .lowFrequencyMethod=${this.lowFrequencyMethod}
        .performanceTradeoff=${this.performanceTradeoff}
        @rr-sample-rate-changed=${this.onSampleRateChange}
        @rr-ppm-changed=${this.onPpmChange}
        @rr-fft-size-changed=${this.onFftSizeChange}
        @rr-fm-deemph-changed=${this.onFmDeemphChange}
        @rr-bias-tee-changed=${this.onBiasTeeChange}
        @rr-low-frequency-method-changed=${this.onLowFrequencyMethodChange}
        @rr-performance-tradeoff-changed=${this.onPerformanceTradeoffChange}
        @rr-window-moved=${this.onWindowMoved}
        @rr-window-closed=${this.onWindowClosed}
      ></rr-settings>

      <rr-presets
        .closed=${!this.windowState.presets.open}
        .size=${this.windowState.presets.size}
        .position=${this.windowState.presets.position}
        .tunedFrequency=${this.frequency.center + this.frequency.offset}
        .tuningStep=${this.tuningStep}
        .scale=${this.scale}
        .availableModes=${getSchemes()}
        .scheme=${this.mode.scheme}
        .bandwidth=${modeParameters(this.mode).getBandwidth()}
        .stereo=${modeParameters(this.mode).getStereo()}
        .squelch=${modeParameters(this.mode).getSquelch()}
        .gain=${this.gain}
        .presets=${this.presets}
        .sortColumn=${this.presetSortColumn}
        @rr-preset-selected=${this.onPresetSelected}
        @rr-presets-changed=${this.onPresetsChanged}
        @rr-presets-sorted=${this.onPresetsSorted}
        @rr-window-moved=${this.onWindowMoved}
        @rr-window-resized=${this.onWindowResized}
        @rr-window-closed=${this.onWindowClosed}
      ></rr-presets>`;
    }
    constructor() {
        super();
        this.availableModes = new Map(getSchemes().map((s) => [s, getMode(s)]));
        this.sampleRate = 1024000;
        this.ppm = 0;
        this.fftSize = 2048;
        this.biasTee = false;
        this.bandwidth = this.sampleRate;
        this.stereoStatus = false;
        this.minDecibels = -90;
        this.maxDecibels = -20;
        this.playing = false;
        this.errorState = false;
        this.needsReload = false;
        this.scale = 1000;
        this.frequency = {
            center: 88500000,
            offset: 0,
            leftBand: 75000,
            rightBand: 75000,
        };
        this.tuningStep = 1000;
        this.mode = this.availableModes.get("WBFM");
        this.gain = null;
        this.gainDisabled = false;
        this.lowFrequencyMethod = {
            name: "default",
            channel: "Q",
            frequency: 100000000,
            biasTee: false,
        };
        this.fmDeemph = 50;
        this.performanceTradeoff = "cpu";
        this.windowState = {
            controls: { position: undefined },
            settings: { open: false, position: undefined },
            presets: { open: false, position: undefined, size: undefined },
        };
        this.presetSortColumn = "frequency";
        this.presets = [];
        this.configProvider = loadConfig();
        this.spectrumPool = new Float32Pool(2, 2048);
        this.spectrum = new Spectrum();
        this.spectrum.size = this.fftSize;
        this.demodulator = new Demodulator(this.getDemodulatorOptions());
        this.sampleCounter = new SampleCounter(20);
        this.radio = new Radio(new RtlProvider(), CompositeReceiver.of(this.spectrum, this.demodulator, this.sampleCounter));
        this.demodulator.setVolume(1);
        this.demodulator.setMode(this.mode);
        this.demodulator.addEventListener("stereo-status", (e) => this.onStereoStatusEvent(e));
        this.radio.addEventListener("radio", (e) => this.onRadioEvent(e));
        this.sampleCounter.addEventListener("sample-click", (e) => this.onSampleClickEvent(e));
    }
    getDemodulatorOptions() {
        let cfg = this.configProvider.get();
        let fmDeemph = cfg.fmDeemph;
        let performanceTradeoff = cfg.performanceTradeoff;
        let latency = performanceTradeoff === "latency";
        let quality = performanceTradeoff === "quality";
        let options = {
            modeOptions: {
                AM: {
                    downsamplerTaps: quality ? 75 : undefined,
                    rfTaps: latency ? 257 : quality ? 75 : undefined,
                    useFftFilter: latency,
                },
                CW: {
                    downsamplerTaps: quality ? 75 : undefined,
                    audioTaps: latency ? 513 : quality ? 95 : undefined,
                    useFftFilter: latency,
                },
                NBFM: {
                    downsamplerTaps: quality ? 75 : undefined,
                    rfTaps: latency ? 257 : quality ? 41 : undefined,
                    useFftFilter: latency,
                },
                USB: {
                    downsamplerTaps: quality ? 75 : undefined,
                    rfTaps: latency ? 257 : quality ? 41 : undefined,
                    useFftFilter: latency,
                },
                LSB: {
                    downsamplerTaps: quality ? 75 : undefined,
                    rfTaps: latency ? 257 : quality ? 75 : undefined,
                    useFftFilter: latency,
                },
                WBFM: {
                    deemphasizerTc: fmDeemph,
                    downsamplerTaps: quality ? 75 : undefined,
                    rfTaps: latency ? 75 : quality ? 75 : undefined,
                    useFftFilter: latency,
                },
            },
        };
        return options;
    }
    connectedCallback() {
        super.connectedCallback();
        this.resizeObserver = new ResizeObserver(() => this.onScreenResize());
        this.resizeObserver.observe(document.body);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.resizeObserver?.disconnect();
    }
    firstUpdated(changed) {
        super.firstUpdated(changed);
        this.applyConfig();
    }
    applyConfig() {
        let cfg = this.configProvider.get();
        for (let modeName of this.availableModes.keys()) {
            let mode = {
                ...this.availableModes.get(modeName),
                ...cfg.modes[modeName],
            };
            this.availableModes.set(modeName, mode);
            if (modeName == cfg.mode)
                this.setMode(mode);
        }
        this.setLowFrequencyMethod(cfg.lowFrequencyMethod);
        this.setCenterFrequency(cfg.centerFrequency);
        this.setTunedFrequency(cfg.tunedFrequency);
        this.tuningStep = cfg.tuningStep;
        this.scale = cfg.frequencyScale;
        this.setGain(cfg.gain);
        this.setSampleRate(cfg.sampleRate);
        this.setPpm(cfg.ppm);
        this.setFftSize(cfg.fftSize);
        this.enableBiasTee(cfg.biasTee);
        this.fmDeemph = cfg.fmDeemph;
        this.performanceTradeoff = cfg.performanceTradeoff;
        this.minDecibels = cfg.minDecibels;
        this.maxDecibels = cfg.maxDecibels;
        this.presetSortColumn = cfg.presets.sortColumn;
        this.presets = cfg.presets.list;
        this.windowState = cfg.windows;
    }
    isFrequencyValid(freq) {
        const leftEdge = freq.offset - freq.leftBand;
        const rightEdge = freq.offset + freq.rightBand;
        return -this.bandwidth / 2 <= leftEdge && rightEdge <= this.bandwidth / 2;
    }
    onSampleClickEvent(e) {
        let spectrum = this.spectrumPool.get(this.spectrum.size);
        this.spectrum.getSpectrum(spectrum);
        this.spectrumView.addFloatSpectrum(this.spectrum.frequency(), spectrum);
    }
    onStart(e) {
        this.bandwidth = this.radio.getSampleRate();
        this.radio.start();
        e.preventDefault();
    }
    onStop(e) {
        this.radio.stop();
        e.preventDefault();
    }
    onScreenResize() {
        this.requestUpdate();
    }
    onPresets() {
        this.changeWindowState((s) => (s.presets.open = true));
    }
    onSettings() {
        this.changeWindowState((s) => (s.settings.open = true));
    }
    changeWindowState(delta) {
        let newState = { ...this.windowState };
        delta(newState);
        this.windowState = newState;
        this.configProvider.update((cfg) => (cfg.windows = this.windowState));
    }
    getWindowName(e) {
        return e === this.mainControlsWindow
            ? "controls"
            : e === this.settingsWindow
                ? "settings"
                : e === this.presetsWindow
                    ? "presets"
                    : undefined;
    }
    onWindowClosed(e) {
        const windowName = this.getWindowName(e.target);
        if (windowName === undefined)
            return;
        const window = e.target;
        const closed = window?.closed;
        if (closed === undefined)
            return;
        this.changeWindowState((s) => (s[windowName].open = !closed));
    }
    onWindowMoved(e) {
        const windowName = this.getWindowName(e.target);
        if (windowName === undefined)
            return;
        const window = e.target;
        const position = window?.position;
        if (!position)
            return;
        this.changeWindowState((s) => (s[windowName].position = position));
    }
    onWindowResized(e) {
        const windowName = this.getWindowName(e.target);
        if (windowName === undefined)
            return;
        const window = e.target;
        const size = window?.size;
        if (!size)
            return;
        this.changeWindowState((s) => (s[windowName].size = size));
    }
    onScaleChange(e) {
        let input = e.target;
        let scale = input.scale;
        this.scale = scale;
    }
    onCenterFrequencyChange(e) {
        let input = e.target;
        let value = input.centerFrequency;
        this.setCenterFrequency(value);
    }
    setCenterFrequency(value) {
        let newFreq = {
            ...this.frequency,
            center: value,
            offset: this.frequency.center + this.frequency.offset - value,
        };
        if (!this.isFrequencyValid(newFreq)) {
            newFreq = { ...newFreq, offset: 0 };
        }
        this.setFrequency(newFreq);
    }
    onTunedFrequencyChange(e) {
        let input = e.target;
        let value = input.tunedFrequency;
        this.setTunedFrequency(value);
    }
    onTuningStepChange(e) {
        let input = e.target;
        let value = input.tuningStep;
        this.tuningStep = value;
        this.configProvider.update((cfg) => (cfg.tuningStep = value));
    }
    setTunedFrequency(value) {
        let newFreq = {
            ...this.frequency,
            offset: value - this.frequency.center,
        };
        if (!this.isFrequencyValid(newFreq)) {
            newFreq = {
                ...newFreq,
                center: newFreq.center + newFreq.offset,
                offset: 0,
            };
        }
        this.setFrequency(newFreq);
    }
    setFrequency(newFreq, force) {
        if (newFreq.center != this.frequency.center || force) {
            let upconverting = newFreq.center < 28800000 &&
                this.lowFrequencyMethod.name == "upconverter";
            let deltaFrequency = upconverting ? this.lowFrequencyMethod.frequency : 0;
            if (newFreq.offset != this.frequency.offset) {
                this.demodulator.expectFrequencyAndSetOffset(newFreq.center + deltaFrequency, newFreq.offset);
            }
            this.radio.setFrequency(newFreq.center + deltaFrequency);
            this.radio.enableBiasTee(this.biasTee || (upconverting && this.lowFrequencyMethod.biasTee));
        }
        else if (newFreq.offset != this.frequency.offset) {
            this.demodulator.setFrequencyOffset(newFreq.offset);
        }
        this.frequency = newFreq;
        this.configProvider.update((cfg) => {
            cfg.centerFrequency = newFreq.center;
            cfg.tunedFrequency = newFreq.center + newFreq.offset;
        });
    }
    onSchemeChange(e) {
        let target = e.target;
        let value = target.scheme;
        let mode = this.availableModes.get(value);
        if (mode === undefined)
            return;
        this.setMode(mode);
    }
    onBandwidthChange(e) {
        let target = e.target;
        let value = target.bandwidth;
        this.setMode(modeParameters(this.mode).setBandwidth(value).mode);
    }
    onStereoChange(e) {
        let target = e.target;
        let value = target.stereo;
        this.setMode(modeParameters(this.mode).setStereo(value).mode);
    }
    onSquelchChange(e) {
        let target = e.target;
        let value = target.squelch;
        this.setMode(modeParameters(this.mode).setSquelch(value).mode);
    }
    setMode(mode) {
        this.demodulator.setMode(mode);
        this.mode = mode;
        this.availableModes.set(mode.scheme, mode);
        this.updateFrequencyBands();
        this.configProvider.update((cfg) => {
            cfg.mode = mode.scheme;
            cfg.modes[mode.scheme] = mode;
        });
    }
    updateFrequencyBands() {
        let bandwidth = modeParameters(this.mode).getBandwidth();
        let newFreq = { ...this.frequency };
        if (this.mode.scheme == "USB") {
            newFreq.leftBand = 0;
            newFreq.rightBand = bandwidth;
        }
        else if (this.mode.scheme == "LSB") {
            newFreq.leftBand = bandwidth;
            newFreq.rightBand = 0;
        }
        else {
            newFreq.leftBand = newFreq.rightBand = bandwidth / 2;
        }
        if (!this.isFrequencyValid(newFreq)) {
            newFreq = {
                ...newFreq,
                center: newFreq.center + newFreq.offset,
                offset: 0,
            };
        }
        this.setFrequency(newFreq);
    }
    onGainChange(e) {
        let target = e.target;
        this.setGain(target.gain);
    }
    setGain(gain) {
        this.radio.setGain(gain);
        this.gain = gain;
        this.configProvider.update((cfg) => (cfg.gain = gain));
    }
    onSampleRateChange(e) {
        let target = e.target;
        this.setSampleRate(target.sampleRate);
    }
    setSampleRate(sampleRate) {
        this.sampleRate = sampleRate;
        this.radio.setSampleRate(sampleRate);
        this.configProvider.update((cfg) => (cfg.sampleRate = sampleRate));
        if (this.radio.isPlaying())
            return;
        this.bandwidth = sampleRate;
        this.setTunedFrequency(this.frequency.center + this.frequency.offset);
    }
    onPpmChange(e) {
        let target = e.target;
        this.setPpm(target.ppm);
    }
    setPpm(ppm) {
        this.radio.setFrequencyCorrection(this.ppm);
        this.ppm = ppm;
        this.configProvider.update((cfg) => (cfg.ppm = ppm));
    }
    onFftSizeChange(e) {
        let target = e.target;
        this.setFftSize(target.fftSize);
    }
    setFftSize(fftSize) {
        this.fftSize = fftSize;
        this.spectrum.size = fftSize;
        this.configProvider.update((cfg) => (cfg.fftSize = fftSize));
    }
    onFmDeemphChange(e) {
        let target = e.target;
        this.configProvider.update((cfg) => (cfg.fmDeemph = target.fmDeemph));
        this.needsReload = true;
    }
    onPerformanceTradeoffChange(e) {
        let target = e.target;
        this.configProvider.update((cfg) => (cfg.performanceTradeoff = target.performanceTradeoff));
        this.needsReload = true;
    }
    onBiasTeeChange(e) {
        let target = e.target;
        this.enableBiasTee(target.biasTee);
    }
    enableBiasTee(biasTee) {
        this.radio.enableBiasTee(biasTee);
        this.biasTee = biasTee;
        this.configProvider.update((cfg) => (cfg.biasTee = biasTee));
    }
    onLowFrequencyMethodChange(e) {
        let target = e.target;
        this.setLowFrequencyMethod(target.lowFrequencyMethod);
    }
    setLowFrequencyMethod(method) {
        let directSampling = method.name != "directSampling"
            ? DirectSampling.Off
            : method.channel == "Q"
                ? DirectSampling.Q
                : DirectSampling.I;
        this.radio.setDirectSamplingMethod(directSampling);
        this.lowFrequencyMethod = { ...method };
        this.setFrequency({ ...this.frequency }, true);
        this.configProvider.update((cfg) => (cfg.lowFrequencyMethod = method));
    }
    onPresetSelected(e) {
        const target = e.target;
        const presetIndex = target.selectedIndex;
        if (presetIndex === undefined)
            return;
        const preset = target.presets[presetIndex];
        this.setTunedFrequency(preset.tunedFrequency);
        this.scale = preset.scale;
        this.tuningStep = preset.tuningStep;
        this.setMode(modeParameters(preset.scheme)
            .setBandwidth(preset.bandwidth)
            .setStereo(preset.stereo)
            .setSquelch(preset.squelch).mode);
        this.setGain(preset.gain);
    }
    onPresetsChanged(e) {
        const target = e.target;
        let presets = [...target.presets];
        this.presets = presets;
        this.configProvider.update((cfg) => (cfg.presets.list = presets));
    }
    onPresetsSorted(e) {
        const target = e.target;
        let sortColumn = target.sortColumn;
        this.presetSortColumn = sortColumn;
        this.configProvider.update((cfg) => (cfg.presets.sortColumn = sortColumn));
    }
    onSpectrumTap(e) {
        this.setTunedFrequencyFraction(e.detail.fraction);
    }
    onSpectrumDrag(e) {
        if (e.detail.operation == "start") {
            this.centerFrequencyScroller?.cancel();
            this.centerFrequencyScroller = new CenterFrequencyScroller(this.bandwidth, this.scale, this.frequency, (f) => this.setFrequency(f));
        }
        else if (e.detail.operation == "cancel") {
            this.centerFrequencyScroller?.cancel();
            this.centerFrequencyScroller = undefined;
        }
        else if (e.detail.operation == "finish") {
            this.centerFrequencyScroller?.finish();
            this.centerFrequencyScroller = undefined;
        }
        else {
            this.centerFrequencyScroller?.drag(e);
        }
    }
    onDecibelRangeChanged(e) {
        let { min, max } = e.detail;
        if (min !== undefined) {
            this.minDecibels = min;
            this.configProvider.update((cfg) => (cfg.minDecibels = min));
        }
        if (max !== undefined) {
            this.maxDecibels = max;
            this.configProvider.update((cfg) => (cfg.maxDecibels = max));
        }
    }
    onSpectrumHighlightChanged(e) {
        if (e.detail.fraction !== undefined) {
            this.setTunedFrequencyFraction(e.detail.fraction);
        }
        else if (e.detail.startFraction !== undefined) {
            this.setSidebandFraction("left", e.detail.startFraction);
        }
        else if (e.detail.endFraction !== undefined) {
            this.setSidebandFraction("right", e.detail.endFraction);
        }
    }
    setTunedFrequencyFraction(fraction) {
        const min = this.frequency.center - this.bandwidth / 2 + this.frequency.leftBand;
        const max = this.frequency.center + this.bandwidth / 2 - this.frequency.rightBand;
        let frequency = Math.max(min, Math.min(this.frequency.center + this.bandwidth * (fraction - 0.5), max));
        frequency = this.tuningStep * Math.round(frequency / this.tuningStep);
        if (frequency < min)
            frequency += this.tuningStep;
        if (frequency > max)
            frequency -= this.tuningStep;
        this.setTunedFrequency(frequency);
    }
    setSidebandFraction(sideband, fraction) {
        const maxLeftSize = Math.floor(this.frequency.offset + this.bandwidth / 2);
        const maxRightSize = Math.floor(this.bandwidth / 2 - this.frequency.offset);
        const size = Math.floor(Math.abs(this.frequency.offset - this.bandwidth * (fraction - 0.5)));
        let modeParams = modeParameters(this.mode);
        switch (this.mode.scheme) {
            case "WBFM":
                return;
            case "NBFM":
            case "AM":
            case "CW":
                modeParams.setBandwidth(Math.min(size, maxLeftSize, maxRightSize) * 2);
                break;
            case "LSB":
                if (sideband == "right")
                    return;
                modeParams.setBandwidth(Math.min(size, maxLeftSize));
                break;
            case "USB":
                if (sideband == "left")
                    return;
                modeParams.setBandwidth(Math.min(size, maxRightSize));
                break;
        }
        this.setMode(modeParams.mode);
    }
    onStereoStatusEvent(e) {
        this.stereoStatus = e.detail;
    }
    onRadioEvent(e) {
        switch (e.detail.type) {
            case "started":
                this.playing = true;
                break;
            case "stopped":
                this.playing = false;
                break;
            case "directSampling":
                this.gainDisabled = e.detail.active;
                break;
            case "error":
                let error = e.detail.exception;
                if (error.type === RadioErrorType.NoDeviceSelected &&
                    error.cause.name === "NotFoundError") {
                    return;
                }
                else if (error.type == RadioErrorType.NoUsbSupport) {
                    alert("This browser does not support the HTML5 USB API. Please try Chrome, Edge, or Opera on a computer or Android.");
                }
                else if (!this.errorState) {
                    this.errorState = true;
                    if (error.cause) {
                        alert(`${error.message}\n\nCaused by: ${error.cause}`);
                    }
                    else {
                        alert(error.message);
                    }
                }
                break;
            default:
            // do nothing
        }
    }
};
__decorate([
    state(),
    __metadata("design:type", Number)
], RadioReceiverMain.prototype, "sampleRate", void 0);
__decorate([
    state(),
    __metadata("design:type", Number)
], RadioReceiverMain.prototype, "ppm", void 0);
__decorate([
    state(),
    __metadata("design:type", Number)
], RadioReceiverMain.prototype, "fftSize", void 0);
__decorate([
    state(),
    __metadata("design:type", Boolean)
], RadioReceiverMain.prototype, "biasTee", void 0);
__decorate([
    state(),
    __metadata("design:type", Number)
], RadioReceiverMain.prototype, "bandwidth", void 0);
__decorate([
    state(),
    __metadata("design:type", Boolean)
], RadioReceiverMain.prototype, "stereoStatus", void 0);
__decorate([
    state(),
    __metadata("design:type", Number)
], RadioReceiverMain.prototype, "minDecibels", void 0);
__decorate([
    state(),
    __metadata("design:type", Number)
], RadioReceiverMain.prototype, "maxDecibels", void 0);
__decorate([
    state(),
    __metadata("design:type", Boolean)
], RadioReceiverMain.prototype, "playing", void 0);
__decorate([
    state(),
    __metadata("design:type", Boolean)
], RadioReceiverMain.prototype, "errorState", void 0);
__decorate([
    state(),
    __metadata("design:type", Boolean)
], RadioReceiverMain.prototype, "needsReload", void 0);
__decorate([
    state(),
    __metadata("design:type", Number)
], RadioReceiverMain.prototype, "scale", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], RadioReceiverMain.prototype, "frequency", void 0);
__decorate([
    state(),
    __metadata("design:type", Number)
], RadioReceiverMain.prototype, "tuningStep", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], RadioReceiverMain.prototype, "mode", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], RadioReceiverMain.prototype, "gain", void 0);
__decorate([
    state(),
    __metadata("design:type", Boolean)
], RadioReceiverMain.prototype, "gainDisabled", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], RadioReceiverMain.prototype, "lowFrequencyMethod", void 0);
__decorate([
    state(),
    __metadata("design:type", Number)
], RadioReceiverMain.prototype, "fmDeemph", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], RadioReceiverMain.prototype, "performanceTradeoff", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], RadioReceiverMain.prototype, "windowState", void 0);
__decorate([
    state(),
    __metadata("design:type", String)
], RadioReceiverMain.prototype, "presetSortColumn", void 0);
__decorate([
    state(),
    __metadata("design:type", Array)
], RadioReceiverMain.prototype, "presets", void 0);
__decorate([
    query("#spectrum"),
    __metadata("design:type", typeof (_a = typeof RrSpectrum !== "undefined" && RrSpectrum) === "function" ? _a : Object)
], RadioReceiverMain.prototype, "spectrumView", void 0);
__decorate([
    query("rr-main-controls"),
    __metadata("design:type", typeof (_b = typeof RrMainControls !== "undefined" && RrMainControls) === "function" ? _b : Object)
], RadioReceiverMain.prototype, "mainControlsWindow", void 0);
__decorate([
    query("rr-settings"),
    __metadata("design:type", typeof (_c = typeof RrSettings !== "undefined" && RrSettings) === "function" ? _c : Object)
], RadioReceiverMain.prototype, "settingsWindow", void 0);
__decorate([
    query("rr-presets"),
    __metadata("design:type", typeof (_d = typeof RrPresets !== "undefined" && RrPresets) === "function" ? _d : Object)
], RadioReceiverMain.prototype, "presetsWindow", void 0);
RadioReceiverMain = __decorate([
    customElement("radioreceiver-main"),
    __metadata("design:paramtypes", [])
], RadioReceiverMain);
export { RadioReceiverMain };
/**
 * Computes the frequency the user is scrolling to, and schedules
 * regular updates to the center frequency because the RTL-SDR
 * stick takes a long time to change frequency.
 */
class CenterFrequencyScroller {
    constructor(bandwidth, scale, frequency, setFrequency) {
        this.bandwidth = bandwidth;
        this.scale = scale;
        this.setFrequency = setFrequency;
        this.original = { ...frequency };
    }
    drag(e) {
        let fraction = e.detail.fraction;
        let delta = -fraction * this.bandwidth;
        let newCenter = this.original.center + delta;
        newCenter = this.scale * Math.round(newCenter / this.scale);
        let newFreq = {
            ...this.original,
            center: newCenter,
            offset: this.original.center + this.original.offset - newCenter,
        };
        if (newFreq.offset - newFreq.leftBand <= -this.bandwidth / 2) {
            newFreq.offset = newFreq.leftBand - this.bandwidth / 2;
        }
        if (this.bandwidth / 2 <= newFreq.offset + newFreq.rightBand) {
            newFreq.offset = this.bandwidth / 2 - newFreq.rightBand;
        }
        this.scheduleFrequencyChange(newFreq);
    }
    cancel() {
        this.newFreq = this.original;
        this.changeFrequency();
    }
    finish() {
        this.changeFrequency();
    }
    scheduleFrequencyChange(newFreq) {
        this.newFreq = newFreq;
        if (this.timeout != undefined)
            return;
        this.timeout = window.setTimeout(() => this.changeFrequency(), 50);
    }
    changeFrequency() {
        if (this.newFreq === undefined)
            return;
        this.setFrequency(this.newFreq);
        this.newFreq = undefined;
        clearTimeout(this.timeout);
        this.timeout = undefined;
    }
}
CreateWindowRegistry();
